import { Component, Inject, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { REG_HUB_COMMON_CONFIG, RegHubCommonConfig } from '../../reg-hub-common-config';
import { EnumeratedTypesRepositoryService } from '../../services/enumerated-types/enumerated-types-repository.service';
import { CorporationAlertsRepositoryServiceService } from '../../services/corporation-alerts/corporation-alerts-repository-service.service';
import { AlertType } from '../../interfaces/enum-types/alert-type';
import { CorporationAlert } from '../../interfaces/corporation-alerts/corporation-alert';
import { CorporationAlertForBatchUpdate } from '../../interfaces/corporation-alerts/corporation-alert-for-batch-update';
import { eAlertSeverity } from '../../enums';
import { ALERT_SEVERITY_OPTIONS, ALERT_TYPE_GROUP_LABELS } from '../../alert-display';

// mat-select treats a null option as "clear the selection", so "use the alert type's default" gets its own value
const DEFAULT_SEVERITY = 'Default';
type SeveritySelection = eAlertSeverity | typeof DEFAULT_SEVERITY;

interface AlertSettingRow {
  alertType: AlertType;
  enabled: boolean;
  severity: SeveritySelection;
  savedEnabled: boolean;
  savedSeverity: SeveritySelection;
}

interface AlertSettingGroup {
  label: string;
  rows: AlertSettingRow[];
}

@Component({
  selector: 'lib-corporation-alert-settings',
  templateUrl: './corporation-alert-settings.component.html',
  styleUrls: ['./corporation-alert-settings.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CorporationAlertSettingsComponent implements OnChanges {
  @Input() corporationID: string | null | undefined;
  // RegHub admins choose which alert types are enabled; client admins only see enabled types and can change their severity
  @Input() canManageEnabled = false;

  readonly defaultSeverity = DEFAULT_SEVERITY;
  readonly severityOptions = ALERT_SEVERITY_OPTIONS;

  loading = false;
  saving = false;
  filterText = '';
  enabledOnly = false;
  statusMessage = '';

  rows: AlertSettingRow[] = [];
  tableRows: (AlertSettingGroup | AlertSettingRow)[] = [];

  constructor(
    private enumeratedTypesRepo: EnumeratedTypesRepositoryService,
    private corporationAlertsRepo: CorporationAlertsRepositoryServiceService,
    private toastr: ToastrService,
    @Inject(REG_HUB_COMMON_CONFIG) private config: RegHubCommonConfig) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['corporationID'] && this.corporationID) {
      this.load();
    }
  }

  get displayedColumns(): string[] {
    return this.canManageEnabled ? ['enabled', 'name', 'severity'] : ['name', 'severity'];
  }

  get changeCount(): number {
    return this.rows.filter(row => this.isRowChanged(row)).length;
  }

  isGroupRow = (_: number, item: AlertSettingGroup | AlertSettingRow): boolean => 'rows' in item;

  // ─── Data ──────────────────────────────────────────────────────────────────

  private load(): void {
    this.loading = true;
    this.statusMessage = '';

    forkJoin({
      alertTypes: this.enumeratedTypesRepo.getAlertTypes(this.config.rootUrl),
      corporationAlerts: this.corporationAlertsRepo.getCorporationAlerts(this.corporationID!, new HttpParams().set('pageSize', 1000))
    }).subscribe({
      next: ({ alertTypes, corporationAlerts }) => {
        this.buildRows(alertTypes, corporationAlerts);
        this.loading = false;
      },
      error: () => {
        this.rows = [];
        this.applyFilter();
        this.loading = false;
        this.toastr.error('Failed to load alert settings');
      }
    });
  }

  private buildRows(alertTypes: AlertType[], corporationAlerts: CorporationAlert[]): void {
    this.rows = [...alertTypes]
      .sort((a, b) => a.sortIndex - b.sortIndex)
      .map((alertType): AlertSettingRow => {
        const corporationAlert = corporationAlerts.find(alert => alert.alertTypeID === alertType.id);
        const enabled = corporationAlert?.enabled ?? false;
        const severity = corporationAlert?.alertSeverityID ?? DEFAULT_SEVERITY;

        return { alertType, enabled, severity, savedEnabled: enabled, savedSeverity: severity };
      })
      .filter(row => this.canManageEnabled || row.savedEnabled);

    this.applyFilter();
  }

  // Rebuilds the visible table. Only runs when the filter changes, so rows don't jump around while being edited.
  applyFilter(): void {
    const filter = this.filterText.trim().toLowerCase();
    const groups = new Map<string, AlertSettingGroup>();

    for (const row of this.rows) {
      const label = ALERT_TYPE_GROUP_LABELS[row.alertType.alertTypeGroupID] ?? row.alertType.alertTypeGroupID;
      const matchesText = !filter || row.alertType.name.toLowerCase().includes(filter) || label.toLowerCase().includes(filter);
      const matchesEnabled = !this.enabledOnly || row.enabled;

      if (matchesText && matchesEnabled) {
        if (!groups.has(label)) {
          groups.set(label, { label, rows: [] });
        }
        groups.get(label)!.rows.push(row);
      }
    }

    this.tableRows = [...groups.values()].flatMap(group => [group, ...group.rows]);
  }

  // ─── Editing ───────────────────────────────────────────────────────────────

  isRowChanged(row: AlertSettingRow): boolean {
    return row.enabled !== row.savedEnabled || row.severity !== row.savedSeverity;
  }

  enabledCount(rows: AlertSettingRow[]): number {
    return rows.filter(row => row.enabled).length;
  }

  isGroupFullyEnabled(group: AlertSettingGroup): boolean {
    return this.enabledCount(group.rows) === group.rows.length;
  }

  isGroupPartiallyEnabled(group: AlertSettingGroup): boolean {
    const enabledCount = this.enabledCount(group.rows);
    return enabledCount > 0 && enabledCount < group.rows.length;
  }

  // Applies to the rows currently shown in the group, so a filtered-out row is never changed without being seen
  setGroupEnabled(group: AlertSettingGroup, enabled: boolean): void {
    group.rows.forEach(row => row.enabled = enabled);
  }

  discardChanges(): void {
    this.rows.forEach(row => {
      row.enabled = row.savedEnabled;
      row.severity = row.savedSeverity;
    });
  }

  save(): void {
    // Snapshot what's being sent, so an edit made while the save is in flight still shows as unsaved afterwards
    const sentRows = this.rows
      .filter(row => this.isRowChanged(row))
      .map(row => ({ row, enabled: row.enabled, severity: row.severity }));

    if (sentRows.length === 0 || this.saving || !this.corporationID) {
      return;
    }

    const corporationAlerts: CorporationAlertForBatchUpdate[] = sentRows.map(sent => ({
      alertTypeID: sent.row.alertType.id,
      enabled: sent.enabled,
      alertSeverityID: sent.severity === DEFAULT_SEVERITY ? null : sent.severity
    }));

    this.saving = true;
    this.statusMessage = '';

    this.corporationAlertsRepo.batchUpdateCorporationAlerts(this.corporationID, corporationAlerts).subscribe({
      next: () => {
        // Mark what was sent as saved rather than reloading, so the table and filter stay as the user left them
        sentRows.forEach(sent => {
          sent.row.savedEnabled = sent.enabled;
          sent.row.savedSeverity = sent.severity;
        });

        this.saving = false;
        this.statusMessage = 'Saved';
      },
      error: () => {
        this.saving = false;
        this.toastr.error('Failed to save alert settings');
      }
    });
  }
}
